// useTema.ts — tema scelto, aspetto (chiaro/scuro/automatico) e applicazione delle variabili CSS.
import { applicaColore } from "./colori.ts";
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { PREFERENZE } from "../costanti.ts";
import { leggiPreferenza, salvaPreferenza } from "../servizi/preferenze.ts";
import { TEMI, TEMA_RIPIEGO, coloriDelTema, eIdTema, type IdTema, type SceltaAspetto, type Colori } from "./temi.ts";
import { caricaCaratteri } from "./caratteri.ts";

const ASPETTI_VALIDI: readonly SceltaAspetto[] = ["automatico", "chiaro", "scuro"];
const QUERY_SCURO = "(prefers-color-scheme: dark)";

function sistemaScuro(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.(QUERY_SCURO).matches === true;
}

function useSistemaScuro(): boolean {
  return useSyncExternalStore(
    (a) => {
      const mq = window.matchMedia?.(QUERY_SCURO);
      mq?.addEventListener("change", a);
      return () => mq?.removeEventListener("change", a);
    },
    sistemaScuro,
  );
}

const NOMI_VARIABILI: Record<keyof Colori, string> = {
  sfondo: "--c-sfondo",
  superficie: "--c-superficie",
  testo: "--c-testo",
  testoTenue: "--c-testo-tenue",
  primario: "--c-primario",
  suPrimario: "--c-su-primario",
  accento: "--c-accento",
  secondario: "--c-secondario",
  bordo: "--c-bordo",
  pericolo: "--c-pericolo",
  successo: "--c-successo",
};

export function applicaTema(id: IdTema, scelta: SceltaAspetto, scuro: boolean): void {
  const tema = TEMI[id];
  caricaCaratteri(id);
  const { aspetto, colori } = coloriDelTema(tema, scelta, scuro);
  const radice = document.documentElement;
  for (const [k, nome] of Object.entries(NOMI_VARIABILI) as [keyof Colori, string][]) {
    radice.style.setProperty(nome, colori[k]);
  }
  radice.style.setProperty("--f-titolo", tema.fontTitolo);
  radice.style.setProperty("--f-testo", tema.fontTesto);
  radice.dataset.tema = id;
  radice.dataset.aspetto = aspetto;
  radice.style.colorScheme = aspetto === "scuro" ? "dark" : "light";
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", colori.sfondo);
  // v021: il colore primario scelto vale sopra quello del tema
  if (id === "laughdown") applicaColore();
}

export function temaIniziale(predefinito: string, disponibili: readonly string[]): IdTema {
  const salvato = leggiPreferenza(PREFERENZE.tema);
  if (eIdTema(salvato) && disponibili.includes(salvato)) return salvato;
  if (eIdTema(predefinito) && disponibili.includes(predefinito)) return predefinito;
  const primo = disponibili.find(eIdTema);
  return primo ?? TEMA_RIPIEGO;
}

function aspettoIniziale(): SceltaAspetto {
  const salvato = leggiPreferenza(PREFERENZE.aspetto);
  return (ASPETTI_VALIDI as readonly string[]).includes(salvato ?? "") ? (salvato as SceltaAspetto) : "automatico";
}

export interface ControlloTema {
  tema: IdTema;
  aspetto: SceltaAspetto;
  scegliTema: (id: IdTema) => void;
  scegliAspetto: (a: SceltaAspetto) => void;
}

/**
 * `disponibili`: i temi che si possono usare ADESSO (accesi dal pannello e permessi dal piano). Se il tema salvato
 * non c'è (es. Silver scaduto) si usa il predefinito, ma la scelta salvata resta: torna quando torna il piano.
 */
export function useTema(predefinito: string, disponibili: readonly string[]): ControlloTema {
  const scuro = useSistemaScuro();
  const [tema, setTema] = useState<IdTema>(() => temaIniziale(predefinito, disponibili));
  const [aspetto, setAspetto] = useState<SceltaAspetto>(aspettoIniziale);

  // Se il server toglie il tema in uso dall'elenco, si passa a uno disponibile.
  useEffect(() => {
    if (!disponibili.includes(tema)) setTema(temaIniziale(predefinito, disponibili));
    else {
      // Il tema salvato è tornato usabile (piano rinnovato, account caricato): si rimette.
      const salvato = leggiPreferenza(PREFERENZE.tema);
      if (eIdTema(salvato) && salvato !== tema && disponibili.includes(salvato)) setTema(salvato);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disponibili, predefinito]);

  useEffect(() => applicaTema(tema, aspetto, scuro), [tema, aspetto, scuro]);

  const scegliTema = useCallback((id: IdTema) => {
    setTema(id);
    salvaPreferenza(PREFERENZE.tema, id);
  }, []);
  const scegliAspetto = useCallback((a: SceltaAspetto) => {
    setAspetto(a);
    salvaPreferenza(PREFERENZE.aspetto, a);
  }, []);

  return { tema, aspetto, scegliTema, scegliAspetto };
}
